import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Tooltip, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import dayjs from "dayjs";

import styles from "../masters/UserManagement.module.css";
import CustomMRTTable from "../../components/CustomMRTTable/CustomMRTTable";
import CustomDatePicker from "../../components/CustomDatePicker/CustomDatePicker";
import AxiosInstance, { IMAGE_BASE_URL } from "../../services/Api";

const BlogList = () => {
  const navigate = useNavigate();

  // ─── Data state ───
  const [blogs, setBlogs] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]); // [{ id, name }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Filter / pagination state ───
  const [columnFilters, setColumnFilters] = useState([]);
  const [apiSearch, setApiSearch] = useState(""); // ✅ committed only on Enter
  const [categoryFilter, setCategoryFilter] = useState(""); // categories_id
  const [statusFilter, setStatusFilter] = useState(""); // "", "1" (Active), "0" (Inactive)
  const [startDateFilter, setStartDateFilter] = useState(""); // YYYY-MM-DD
  const [endDateFilter, setEndDateFilter] = useState(""); // YYYY-MM-DD
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ─── Fetch category list once (GET /blog/categorieslist) ───
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await AxiosInstance.get("/blog/categorieslist");
        const list = response?.data?.tag || [];
        setCategoryOptions(
          list.map((c) => ({ id: c.id, name: c.name })),
        );
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategoryOptions([]);
      }
    };
    fetchCategories();
  }, []);

  // ─── Fetch blog list (POST /blog/list) ───
  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        blog_status: statusFilter, // "", "1", "0"
        tag_id: "",
        author_name: "",
        author_designation: "",
        categories_id: categoryFilter, // id or ""
        start_date: startDateFilter, // YYYY-MM-DD or ""
        end_date: endDateFilter, // YYYY-MM-DD or ""
        search: apiSearch,
      };

      const response = await AxiosInstance.post("/blog/list", payload);
      const apiList = response?.data?.blog || [];

      if (response?.data?.status && Array.isArray(apiList)) {
        const mappedList = apiList.map((item) => ({
          ...item,
          author: item?.author_name || "",
          designation: item?.author_designation || "",
          category:
            item?.categorie_name ||
            item?.category
              ?.map((c) => c?.category_name)
              .filter(Boolean)
              .join(", ") ||
            "",
          tags: Array.isArray(item?.tag)
            ? item.tag.map((t) => t?.tag_name).filter(Boolean)
            : [],
        }));
        setBlogs(mappedList);
      } else {
        setBlogs([]);
        throw new Error(response?.data?.message || "Failed to fetch blog list");
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError(err?.message || "An error occurred");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [apiSearch, categoryFilter, statusFilter, startDateFilter, endDateFilter]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // ─── View handler (live URL or fallback to internal preview) ───
  const handleView = useCallback((row) => {
    const livePath = String(row?.live_file_path || "").trim();

    if (livePath && livePath !== "0") {
      let url;
      if (livePath.startsWith("http://") || livePath.startsWith("https://")) {
        url = livePath;
      } else {
        const baseURL = AxiosInstance?.defaults?.baseURL || "";
        const origin = (() => {
          try {
            return new URL(baseURL).origin;
          } catch {
            return IMAGE_BASE_URL.replace(/\/+$/, "");
          }
        })();
        url = `${origin}${livePath.startsWith("/") ? livePath : `/${livePath}`}`;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    window.open(`/blogView?id=${row?.id}`, "_blank", "noopener,noreferrer");
  }, []);

  const handleEdit = useCallback(
    (row) => {
      navigate(`/dashboard/blogCreate?id=${row?.id}`);
    },
    [navigate],
  );

  // ─── Column filter state — dropdowns + date commit immediately, text only on Enter ───
  const handleColumnFiltersChange = (updaterOrValue) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;
    setColumnFilters(newFilters);

    // ✅ Category dropdown commits immediately
    const catEntry = newFilters.find((f) => f.id === "category");
    const newCatName = catEntry?.value ?? "";
    const matchedCat = categoryOptions.find((c) => c.name === newCatName);
    const newCategoryId = matchedCat ? matchedCat.id : "";

    if (newCategoryId !== categoryFilter) {
      setCategoryFilter(newCategoryId);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }

    // ✅ Status dropdown commits immediately
    const statusEntry = newFilters.find((f) => f.id === "blog_status");
    const newStatus = statusEntry?.value ?? "";
    const mappedStatus =
      newStatus === "Active" ? 1 : newStatus === "Inactive" ? 0 : "";

    if (mappedStatus !== statusFilter) {
      setStatusFilter(mappedStatus);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }

    // ✅ Posted Date range filter commits immediately
    const dateEntry = newFilters.find((f) => f.id === "posted_date");
    const dateVal = dateEntry?.value || {};
    const newStart = dateVal.start || "";
    const newEnd = dateVal.end || "";

    if (newStart !== startDateFilter) {
      setStartDateFilter(newStart);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
    if (newEnd !== endDateFilter) {
      setEndDateFilter(newEnd);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  };

  // ─── Only Enter commits the text search → triggers API ───
  const handleFilterKeyDown = (e) => {
    if (e.key === "Enter") {
      const inputValue = e.target.value || "";
      setApiSearch(inputValue.trim());
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  };

  // ─── Date Range Filter component (start_date + end_date) ───
// ─── Date Range Filter component (start_date + end_date) ───
  const DateRangeFilter = ({ column }) => {
    const filterValue = column.getFilterValue() || {};
    const [localStart, setLocalStart] = useState(filterValue.start || "");
    const [localEnd, setLocalEnd] = useState(filterValue.end || "");

    useEffect(() => {
      setLocalStart(filterValue.start || "");
      setLocalEnd(filterValue.end || "");
    }, [filterValue.start, filterValue.end]);

    const startValue = localStart ? dayjs(localStart) : null;
    const endValue = localEnd ? dayjs(localEnd) : null;

    const handleStartChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalStart(formatted);

      // ✅ If existing End Date is before the new Start Date → clear End Date
      let nextEnd = localEnd;
      if (formatted && localEnd && dayjs(localEnd).isBefore(dayjs(formatted), "day")) {
        nextEnd = "";
        setLocalEnd("");
      }

      const nextValue =
        !formatted && !nextEnd ? undefined : { start: formatted, end: nextEnd };
      column.setFilterValue(nextValue);
    };

    const handleEndChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalEnd(formatted);
      const nextValue =
        !localStart && !formatted ? undefined : { start: localStart, end: formatted };
      column.setFilterValue(nextValue);
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          minWidth: "180px",
        }}
      >
        <CustomDatePicker
          name="start_date"
          value={startValue}
          onChange={handleStartChange}
          placeholder="From Date"
          format="DD/MM/YYYY"
          size="sm"
          isClearable={true}
          // ✅ Start Date cannot be after the already-picked End Date
          maxDate={endValue || undefined}
        />
        <CustomDatePicker
          name="end_date"
          value={endValue}
          onChange={handleEndChange}
          placeholder="To Date"
          format="DD/MM/YYYY"
          size="sm"
          isClearable={true}
          // ✅ To Date enabled ONLY from Start Date onwards — earlier dates disabled
          minDate={startValue || undefined}
          // ✅ Disable entire To Date picker until From Date is picked (optional but cleaner UX)
          disabled={!startValue}
        />
      </div>
    );
  };
  // ─── Client-side pagination (API returns full list) ───
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return blogs.slice(start, end);
  }, [blogs, pagination.pageIndex, pagination.pageSize]);

  // ─── Excel download (current page data only) ───
  const handleExcelDownload = async () => {
    try {
      if (!paginatedData || paginatedData.length === 0) return;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Blog List";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet("Blog List");

      // Define columns + widths
      worksheet.columns = [
        { header: "S No", key: "sno", width: 8 },
        { header: "Title", key: "title", width: 45 },
        { header: "Tagline", key: "tagline", width: 50 },
        { header: "Meta Title", key: "metaTitle", width: 45 },
        { header: "Meta Description", key: "metaDescription", width: 55 },
        { header: "Author Name", key: "authorName", width: 22 },
        { header: "Author Designation", key: "authorDesignation", width: 32 },
        { header: "Posted By Name", key: "postedByName", width: 22 },
        { header: "Posted Date", key: "postedDate", width: 16 },
        { header: "Blog Status (Active / Inactive)", key: "blogStatus", width: 28 },
        { header: "Tags", key: "tags", width: 30 },
        { header: "Category", key: "category", width: 28 },
      ];

      // ── Style header row (bold + background + border + center) ──
      const headerRow = worksheet.getRow(1);
      headerRow.height = 24;
      headerRow.eachCell((cell) => {
        cell.font = {
          bold: true,
          size: 11,
          color: { argb: "FF000000" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE0E0E0" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF999999" } },
          left: { style: "thin", color: { argb: "FF999999" } },
          bottom: { style: "thin", color: { argb: "FF999999" } },
          right: { style: "thin", color: { argb: "FF999999" } },
        };
      });

      // ── Add data rows from CURRENT page only ──
      paginatedData.forEach((item, index) => {
        worksheet.addRow({
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          title: item.title || "",
          tagline: item.tagline || "",
          metaTitle: item.meta_title || "",
          metaDescription: item.meta_description || "",
          authorName: item.author_name || "",
          authorDesignation: item.author_designation || "",
          postedByName: item.posted_by_name || "",
          postedDate: item.posted_date
            ? moment(item.posted_date).format("DD/MM/YYYY")
            : "",
          blogStatus: Number(item.blog_status) === 1 ? "Active" : "Inactive",
          tags: Array.isArray(item.tags) ? item.tags.join(", ") : "",
          category: item.category || "",
        });
      });

      // ── Style data rows ──
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // skip header (already styled)
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFCCCCCC" } },
            left: { style: "thin", color: { argb: "FFCCCCCC" } },
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
            right: { style: "thin", color: { argb: "FFCCCCCC" } },
          };
          cell.alignment = {
            vertical: "middle",
            horizontal: colNumber === 1 ? "center" : "left",
            wrapText: true,
          };
          cell.font = { size: 10 };
        });
      });

      // ── Generate and download ──
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `BLOG_LIST_Page_${pagination.pageIndex + 1}.xlsx`);
    } catch (err) {
      console.error("Error generating Excel:", err);
    }
  };

  // ─── Columns: S.No → Title → Author → Category → Posted Date → Status → Actions ───
  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "title",
        id: "title",
        header: "Title",
        size: 240,
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: "Search Title",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "author",
        header: "Author Name",
        size: 180,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const name = row?.original?.author || "—";
          const designation = row?.original?.designation;
          return designation ? `${name} (${designation})` : name;
        },
      },
      {
        accessorKey: "category",
        id: "category",
        header: "Category",
        size: 200,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: categoryOptions.map((c) => c.name),
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "posted_date",
        id: "posted_date",
        header: "Posted Date",
        size: 220,
        enableSorting: true,
        enableColumnFilter: true,
        Filter: ({ column }) => <DateRangeFilter column={column} />,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return v ? moment(v).format("DD/MM/YYYY") : "—";
        },
      },
      {
        accessorKey: "blog_status",
        id: "blog_status",
        header: "Status",
        size: 120,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: ["Active", "Inactive"],
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          const isActive = value === 1;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                background: isActive ? "#E8F5E9" : "#FFEBEE",
                color: isActive ? "#2E7D32" : "#C62828",
                border: `1px solid ${isActive ? "#A5D6A7" : "#EF9A9A"}`,
              }}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Tooltip title="View" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(row.original);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit" arrow>
              <IconButton
                size="small"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row.original);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      categoryOptions,
      handleEdit,
      handleView,
      pagination.pageIndex,
      pagination.pageSize,
    ],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>BLOG LIST</h1>
        <div
          className={styles.headerActions}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "nowrap",
          }}
        >
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => navigate("/dashboard/blogCreate")}
            aria-label="Create Blog"
            title="Create Blog"
            style={{
              width: "auto",
              padding: "0 10px",
              gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            <i className="fa-solid fa-plus" />
            <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              Create Blog
            </span>
          </button>

          {/* Excel download */}
          <Tooltip title="Download Excel" arrow>
            <IconButton
              size="medium"
              onClick={handleExcelDownload}
              className={styles.menuButton}
              aria-label="Download Excel"
              disabled={loading || paginatedData.length === 0}
            >
              <i
                className="fa-solid fa-download"
                style={{ fontSize: "18px", color: "#333" }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* {error && (
        <div className={styles.errorMessage}>
          {error}
          <button onClick={fetchBlogs} className={styles.retryButton}>
            Retry
          </button>
        </div>
      )} */}

      <div className={styles.tableSection}>
        <CustomMRTTable
          columns={columns}
          data={paginatedData}
          loading={loading}
          // client-side pagination, server-side filtering
          manualPagination={false}
          manualFiltering={true}
          rowCount={blogs.length}
          pagination={pagination}
          onPaginationChange={setPagination}
          // ✅ column-wise filters — text on Enter, dropdowns + date on change
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          // toggles
          enablePagination
          enableSorting
          enableColumnFilters={true}
          enableSearch={false}
          enableColumnVisibility
          enableDensityToggle
          enableFullScreenToggle
          enableHiding
          enableStickyHeader
        />
      </div>
    </div>
  );
};

export default BlogList;